import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { GOTO_KEYS } from './commands.js';
import { viewHref } from './sections.js';

/**
 * Global keyboard shortcuts of the shell (docs/05 §6.1): `⌘K`/`Ctrl+K` opens the palette, `N`
 * opens the quick capture (CAP-01), `G` then a letter goes to a view, `?` opens the cheat sheet and `Esc` closes overlays. Everything
 * except `⌘K` is off while the focus is in a text field, so typing «Gases» never navigates.
 */

/** Time window for the second key of a `G` sequence. */
export const SEQUENCE_MS = 1200;

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (target as HTMLInputElement).type;
  // Checkboxes and radios take keys too, but typing letters in them is not writing text.
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color'].includes(type);
}

export interface ShortcutHandlers {
  enabled: boolean;
  openPalette: () => void;
  openCapture: () => void;
  openShortcuts: () => void;
  closeOverlays: () => void;
}

export function useShortcuts({
  enabled,
  openPalette,
  openCapture,
  openShortcuts,
  closeOverlays,
}: ShortcutHandlers) {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);
  const handlers = useRef({ openPalette, openCapture, openShortcuts, closeOverlays });
  // Latest handlers for the single listener, updated after each render (never during it).
  useEffect(() => {
    handlers.current = { openPalette, openCapture, openShortcuts, closeOverlays };
  });

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      // The palette works from anywhere, text fields included, like in every editor.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        handlers.current.openPalette();
        return;
      }
      if (event.key === 'Escape') {
        handlers.current.closeOverlays();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      const key = event.key.toLowerCase();
      if (pendingG.current !== null && Date.now() - pendingG.current <= SEQUENCE_MS) {
        pendingG.current = null;
        const view = GOTO_KEYS[key];
        if (view) {
          event.preventDefault();
          void navigate({ to: viewHref(view) });
        }
        return;
      }
      pendingG.current = null;

      if (key === 'g') {
        pendingG.current = Date.now();
        return;
      }
      if (key === 'n') {
        // Without this the «n» would land in the field the capture focuses right away.
        event.preventDefault();
        handlers.current.openCapture();
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        handlers.current.openShortcuts();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, navigate]);
}
